import {
  HeicDecodeError,
  ImageDecodeError,
  isHeicFile,
  isPdfFile,
  isRejectedPhotoUploadType,
  UnsupportedUploadTypeError,
} from './photoFiles';

/**
 * Give a compressed file the `.jpg` name it has actually earned.
 *
 * The canvas re-encodes EVERYTHING to JPEG, so a PNG screenshot or an iOS HEIC
 * arrives at the API as `image/jpeg` bytes. Keeping the source name meant
 * uploading mimetype `image/jpeg` under the filename `screenshot.png`, and the
 * API's `ensureUploadExtension` — which appends rather than rewrites, so that a
 * PDF misnamed `.jpg` cannot pass itself off as an image — would then store
 * `screenshot.png.jpg` and `IMG_1234.HEIC.jpg` forever.
 *
 * Fixing it here rather than loosening the append rule keeps that backstop
 * intact: the name is corrected where the type actually changes.
 */
function withJpegExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  return `${stem}.jpg`;
}

/**
 * Decode a file with the browser's own image decoder.
 *
 * Rejects with `ImageDecodeError` rather than an English `Error`, because that
 * message is shown: several upload call sites render `err.message` directly,
 * and `describeUploadFailures` drops anything it does not recognise as Dutch.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageDecodeError());
    };
    img.src = url;
  });
}

/**
 * Decode an iPhone HEIC with libheif, compiled to WebAssembly.
 *
 * Only Safari can decode HEIC natively. Everywhere else — the Windows desktops
 * in the office, Chrome on Android — an iPhone photo failed at `new Image()`,
 * so a user who had switched her iPhone camera to "High Efficiency" could not
 * upload a single photo. Converting server-side would mean shipping a codec
 * into the API for a format only the browser ever sees; `sharp` is already a
 * dependency there and its bundled libvips refuses HEVC.
 *
 * Loaded through a dynamic import on purpose: this line is the only thing that
 * pulls the decoder, so its ~2 MB is fetched the first time someone actually
 * picks a HEIC and never in a normal session. `heicDecoder` spreads the work
 * over a few workers and yields an `ImageBitmap`, which goes straight onto the
 * canvas — no intermediate JPEG, so the photo is encoded lossily only once.
 */
async function decodeHeic(file: File): Promise<ImageBitmap> {
  try {
    const { decodeHeicToBitmap } = await import('./heicDecoder');
    return await decodeHeicToBitmap(file);
  } catch {
    throw new HeicDecodeError();
  }
}

/** Scale to fit `maxWidth` and re-encode as JPEG. */
function encodeJpeg(
  source: HTMLImageElement | ImageBitmap,
  filename: string,
  maxWidth: number,
  quality: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    let { width, height } = source;
    if (width > maxWidth) {
      height = Math.round((height * maxWidth) / width);
      width = maxWidth;
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('Canvas not supported'));
    ctx.drawImage(source, 0, 0, width, height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Compression failed'));
        resolve(new File([blob], filename, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      quality,
    );
  });
}

export async function compressImage(file: File, maxWidth = 1920, quality = 0.8): Promise<File> {
  // Reject a type the API would refuse anyway, with a reason the caller can show.
  // Without this the canvas fails first: `new Image()` cannot decode an HTML or
  // Word file, `onerror` fires, and the only thing that survives is an opaque
  // English "Failed to load image" that the failure counter swallows.
  if (isRejectedPhotoUploadType(file)) throw new UnsupportedUploadTypeError();

  // A PDF cannot go through `new Image()` -> <canvas>: `img.onerror` fires and the
  // upload dies in the browser before it ever reaches the API. Photo-style uploads
  // accept PDF (a drukattest off the pressure-test device), so pass it through
  // untouched. Every upload call site funnels through here, so this one guard
  // covers collector, drukattest, EasyKit, interventie and installer uploads.
  if (isPdfFile(file)) return file;

  // Native decoder first, libheif only for a HEIC it refused: Safari reads HEIC
  // by itself, and a `.heic` that is secretly a JPEG is read by everyone. Either
  // way the 3 MB decoder stays undownloaded.
  let source: HTMLImageElement | ImageBitmap;
  try {
    source = await loadImage(file);
  } catch (err) {
    if (!isHeicFile(file)) throw err;
    source = await decodeHeic(file);
  }

  try {
    return await encodeJpeg(source, withJpegExtension(file.name), maxWidth, quality);
  } finally {
    // A full-resolution ImageBitmap is tens of megabytes of decoded pixels and
    // is not reclaimed by GC alone; an HTMLImageElement has no close().
    if ('close' in source) source.close();
  }
}
